import React, { useState, Fragment } from "react";
import moment from "moment";

import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import _ from "lodash";
import PencilOutline from "mdi-material-ui/PencilOutline";
import DeleteOutline from "mdi-material-ui/DeleteOutline";
import CustomChip from "@core/components/mui/chip";
import QuickSearchToolbar from "views/components/QuickSearchToolbar";
import DialogModal from "@core/components/dialogmodal";
import { isPermissionAllow } from "@core/utils/IsPermissionAllow";
import Check from "mdi-material-ui/Check";

const UserList = (props) => {
  const {
    handleDeleteModal,
    deleteOpen,
    handleOpen,
    userList,
    pageSize,
    searchText,
    pageNumber,
    setPageSize,
    setSearchText,
    setPageNumber,
    totalRecord,
    permission,
    role,
    approvalFilter,
    setApprovalFilter,
    handleApproveUser,
  } = props;

  const [deleteId, setDeleteId] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const theme = useTheme();

  const handleEdit = (id) => {
    handleOpen("edit", id);
  };

  const handleDelete = (id) => {
    handleDeleteModal();
    setDeleteId(id);
  };

  const statusObj = {
    0: {
      title: "Pending",
      color: "warning",
    },
    1: {
      title: "Approved",
      color: "success",
    },
  };

  const columns = [
    {
      flex: 0.5,
      minWidth: 80,
      field: "index",
      headerName: "Index",
    },
    {
      flex: 1,
      minWidth: 130,
      field: "name",
      headerName: "Name",
    },
    {
      flex: 1,
      minWidth: 200,
      field: "email",
      headerName: "Email",
    },
    {
      flex: 1,
      minWidth: 150,
      field: "phone_no",
      headerName: "phone",
      renderCell: ({ row }) => {
        return (
          <Box>
            {row?.country_code && row?.phone_no && (
              <Typography sx={{ fontSize: "0.9rem !important" }}>
                +{row.country_code}-{row.phone_no}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      flex: 1,
      minWidth: 130,
      field: "member_type",
      headerName: "Member type",
      renderCell: ({ row }) => {
        const type = row.member_type;
        return (
          <Box>
            <Button
              type="reset"
              variant="contained"
              size="small"
              sx={{ pointerEvents: "none", fontSize: "0.8rem !important" }}
              style={{
                backgroundColor:
                  type === 1
                    ? theme.palette.primary.light
                    : theme.palette.text.disabled,
              }}
            >
              member
            </Button>
          </Box>
        );
      },
    },
    {
      flex: 1,
      minWidth: 130,
      field: "approval_status",
      headerName: "Approval status",
      renderCell: ({ row }) => {
        const status = statusObj[row.approval_status];

        return (
          <CustomChip
            size="small"
            skin="light"
            color={status.color}
            label={status.title}
            sx={{ "& .MuiChip-label": { textTransform: "capitalize" } }}
          />
        );
      },
    },
    {
      flex: 1,
      minWidth: 200,
      field: "created_at",
      headerName: "Create date",
      renderCell: ({ row }) => {
        const formatDate = moment(row.created_at).format("DD-MM-yyyy HH:mm:ss");
        return (
          <Typography sx={{ fontSize: "0.9rem !important" }}>
            {formatDate}
          </Typography>
        );
      },
    },
    {
      flex: 0.8,
      minWidth: 120,
      field: "action",
      headerName: "Actions",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              {((permission.edit && role === "2") || role === "1") &&
                Number(row.approval_status) === 0 && (
                  <Tooltip title="Approve user">
                    <IconButton
                      onClick={() => handleApproveUser(row.user_id)}
                      sx={{ color: theme.palette.success.main }}
                    >
                      <Check fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                )}
              {((permission.edit && role === "2") || role === "1") && (
                <IconButton onClick={() => handleEdit(row.user_id)}>
                  <PencilOutline fontSize="medium" />
                </IconButton>
              )}
              {((permission.delete && role === "2") || role === "1") && (
                <IconButton onClick={() => handleDelete(row.id)}>
                  <DeleteOutline fontSize="medium" />
                </IconButton>
              )}
            </Box>
          </Box>
        );
      },
    },
  ];
  const removeaction =
    !permission.edit && !permission.delete && role === "2"
      ? _.dropRight(columns)
      : columns;

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPageNumber(0);
  };

  const handleApprovalFilterChange = (event) => {
    const value = event.target.value;
    setApprovalFilter(value === "" ? "" : Number(value));
    setPageNumber(0);
  };

  const assemList = userList.map((data, index) => {
    return {
      index: index + pageSize * pageNumber + 1,
      id: data.id,
      user_id: data.user_id,
      name: data.first_name + " " + (data?.last_name ? data?.last_name : ""),
      status: data.status,
      email: data.email,
      phone_no: data.phone_no,
      country_code: data.country_code,
      member_type: data.member_type,
      approval_status: data.approval_status,
      created_at: data.created_at,
    };
  });

  return (
    <Fragment>
      <Card sx={{ width: "100%", overflowInline: "hidden" }}>
        <DataGrid
          hideFooterSelectedRowCount
          autoHeight
          localeText={{ noRowsLabel: "No results found" }}
          columns={removeaction}
          rows={assemList}
          page={pageNumber}
          onPageChange={(newPageNumber) => setPageNumber(newPageNumber)}
          pageSize={pageSize}
          rowCount={Number(totalRecord)}
          rowsPerPageOptions={[10, 25, 50]}
          components={{ Toolbar: QuickSearchToolbar }}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSelectionModelChange={(rows) => setSelectedRows(rows)}
          checkboxSelection={isPermissionAllow(permission.delete, role)}
          disableSelectionOnClick
          componentsProps={{
            toolbar: {
              value: searchText,
              clearSearch: () => handleSearch(""),
              onChange: (event) => handleSearch(event.target.value),
              handleOpen: () => handleOpen("add", ""),
              title: "",
              selectedRows: selectedRows,
              multiDelete: isPermissionAllow(permission.delete, role),
              onClickDelete: () => handleDeleteModal(),
              approvalFilter: true,
              approvalFilterValue: approvalFilter,
              onChangeApprovalFilter: handleApprovalFilterChange,
            },
          }}
          sx={{
            ".MuiTablePagination-displayedRows": {
              marginTop: "1rem",
            },
            ".MuiTablePagination-selectLabel": {
              marginTop: "1.1rem",
            },
            ".MuiInputBase-root": {
              marginTop: "0.2rem",
            },
          }}
          pagination
          paginationMode="server"
        />
      </Card>

      {deleteOpen && (
        <DialogModal
          open={deleteOpen}
          handleClose={() => handleDeleteModal()}
          handleDelete={() =>
            handleDeleteModal(
              selectedRows && selectedRows.length === 0
                ? [deleteId]
                : selectedRows,
            )
          }
        />
      )}
    </Fragment>
  );
};

export default UserList;
